import type { CSSProperties } from 'react'
import type * as ExcelJs from 'exceljs'
import type { Border, Cell, Workbook, Worksheet } from 'exceljs'

import {
  columnLetter,
  cssColor,
  decodeAddress,
  decodeRange,
  displayText,
  formulaBarText,
  isDateFormat,
  plainValue,
} from './excel-format'

/**
 * A worksheet reduced to what the preview draws: column widths, row heights,
 * and each cell's text and style, with merges and Excel's text spill resolved.
 */

/** Rows drawn per sheet. Past this the DOM gets slow; the download holds them all. */
export const PREVIEW_ROW_LIMIT = 500

/** Excel's default column (8.43 characters) and row (15pt) sizes, in pixels. */
const DEFAULT_WIDTH = 64
const DEFAULT_HEIGHT = 20

export interface GridCell {
  column: number
  text: string
  /** The cell's content as the formula bar shows it. */
  input: string
  style: CSSProperties
  colSpan: number
  rowSpan: number
  /** Covered by a merge or by the text spilling in from its left. Not drawn. */
  covered: boolean
  /** The header row of an AutoFilter range carries the dropdown button. */
  filter: boolean
}

export interface GridRow {
  number: number
  height: number
  cells: GridCell[]
}

export interface GridSheet {
  name: string
  columns: { letter: string; width: number }[]
  rows: GridRow[]
  totalRows: number
  frozenRows: number
  frozenColumns: number
  gridLines: boolean
}

/** Excel's width unit is characters of the default font; this is its own conversion. */
const columnPixels = (width: number | undefined) =>
  width === undefined ? DEFAULT_WIDTH : Math.round(width * 7 + 5)

const pointsToPixels = (points: number) => Math.round((points * 4) / 3)

function borderCss(edge: Partial<Border> | undefined): string | undefined {
  if (!edge?.style) return undefined

  const color = cssColor(edge.color) ?? '#000000'
  switch (edge.style) {
    case 'double':
      return `3px double ${color}`
    case 'medium':
    case 'mediumDashed':
    case 'mediumDashDot':
    case 'mediumDashDotDot':
      return `2px solid ${color}`
    case 'thick':
      return `3px solid ${color}`
    case 'dotted':
    case 'hair':
      return `1px dotted ${color}`
    case 'dashed':
    case 'dashDot':
    case 'dashDotDot':
    case 'slantDashDot':
      return `1px dashed ${color}`
    default:
      return `1px solid ${color}`
  }
}

function cellStyle(cell: Cell, numeric: boolean, isBoolean: boolean): CSSProperties {
  const { font, fill, alignment, border } = cell
  const style: CSSProperties = {}

  if (font?.name) style.fontFamily = `"${font.name}", Calibri, Arial, sans-serif`
  if (font?.size) style.fontSize = `${pointsToPixels(font.size)}px`
  if (font?.bold) style.fontWeight = 700
  if (font?.italic) style.fontStyle = 'italic'
  if (font?.underline || font?.strike) {
    style.textDecoration = [font.underline && 'underline', font.strike && 'line-through'].filter(Boolean).join(' ')
  }
  if (font?.color) style.color = cssColor(font.color)

  if (fill?.type === 'pattern' && fill.pattern !== 'none') {
    style.backgroundColor = cssColor(fill.fgColor) ?? cssColor(fill.bgColor)
  } else if (fill?.type === 'gradient') {
    style.backgroundColor = cssColor(fill.stops[0]?.color)
  }

  const horizontal = alignment?.horizontal
  style.textAlign =
    horizontal === 'center' || horizontal === 'centerContinuous'
      ? 'center'
      : horizontal === 'right'
        ? 'right'
        : horizontal === 'left' || horizontal === 'fill'
          ? 'left'
          : horizontal === 'justify' || horizontal === 'distributed'
            ? 'justify'
            : isBoolean
              ? 'center'
              : numeric
                ? 'right'
                : 'left'

  style.verticalAlign =
    alignment?.vertical === 'top' ? 'top' : alignment?.vertical === 'middle' ? 'middle' : 'bottom'

  if (alignment?.wrapText) style.whiteSpace = 'pre-wrap'
  if (alignment?.indent) style.paddingLeft = `${alignment.indent * 9 + 3}px`

  if (border) {
    style.borderTop = borderCss(border.top)
    style.borderBottom = borderCss(border.bottom)
    style.borderLeft = borderCss(border.left)
    style.borderRight = borderCss(border.right)
  }

  return style
}

/** Rough width of a run of text in the cell's font — enough to decide how far it spills. */
function textPixels(text: string, style: CSSProperties): number {
  const size = Number.parseFloat(String(style.fontSize ?? '15')) || 15
  return text.length * size * (style.fontWeight === 700 ? 0.6 : 0.55)
}

function readSheet(sheet: Worksheet): GridSheet {
  const columnCount = Math.max(sheet.columnCount, 1)
  const totalRows = sheet.rowCount
  const rowCount = Math.min(totalRows, PREVIEW_ROW_LIMIT)

  const columns = Array.from({ length: columnCount }, (_, i) => {
    const column = sheet.getColumn(i + 1)
    return { letter: columnLetter(i + 1), width: column.hidden ? 0 : columnPixels(column.width) }
  })

  const view = sheet.views?.[0] as { state?: string; xSplit?: number; ySplit?: number; showGridLines?: boolean } | undefined
  const frozen = view?.state === 'frozen'

  let filterRow = 0
  let filterLeft = 0
  let filterRight = 0
  const autoFilter = sheet.autoFilter as string | { from: string | { row: number; column: number }; to: string | { row: number; column: number } } | undefined
  if (typeof autoFilter === 'string') {
    const range = decodeRange(autoFilter)
    ;[filterRow, filterLeft, filterRight] = [range.top, range.left, range.right]
  } else if (autoFilter) {
    const from = typeof autoFilter.from === 'string' ? decodeAddress(autoFilter.from) : autoFilter.from
    const to = typeof autoFilter.to === 'string' ? decodeAddress(autoFilter.to) : autoFilter.to
    ;[filterRow, filterLeft, filterRight] = [from.row, from.column, to.column]
  }

  const rows: GridRow[] = []

  for (let r = 1; r <= rowCount; r++) {
    const row = sheet.getRow(r)
    const cells: GridCell[] = []

    for (let c = 1; c <= columnCount; c++) {
      const cell = row.getCell(c)
      const value = plainValue(cell.value)
      const numFmt = cell.numFmt
      const numeric = typeof value === 'number' || value instanceof Date

      cells.push({
        column: c,
        text: displayText(value, value instanceof Date || isDateFormat(numFmt) || typeof value === 'number' ? numFmt : undefined),
        input: formulaBarText(cell.value, numFmt),
        style: cellStyle(cell, numeric, typeof value === 'boolean'),
        colSpan: 1,
        rowSpan: 1,
        covered: false,
        filter: r === filterRow && c >= filterLeft && c <= filterRight,
      })
    }

    rows.push({
      number: r,
      height: row.hidden ? 0 : row.height ? pointsToPixels(row.height) : DEFAULT_HEIGHT,
      cells,
    })
  }

  // Merges: the top-left cell spans the range, the rest are covered.
  const merged = new Set<string>()
  const merges = (sheet.model as { merges?: string[] }).merges ?? []
  for (const range of merges) {
    const { top, left, bottom, right } = decodeRange(range)
    const master = rows[top - 1]?.cells[left - 1]
    if (!master) continue

    master.rowSpan = Math.min(bottom, rowCount) - top + 1
    master.colSpan = Math.min(right, columnCount) - left + 1

    for (let r = top; r <= Math.min(bottom, rowCount); r++) {
      for (let c = left; c <= Math.min(right, columnCount); c++) {
        merged.add(`${r}:${c}`)
        if (r !== top || c !== left) rows[r - 1].cells[c - 1].covered = true
      }
    }
  }

  // Spill: unwrapped left-aligned text runs on over the empty cells to its right,
  // which is how a title in A1 reads across the whole sheet in Excel.
  for (const row of rows) {
    for (const cell of row.cells) {
      if (cell.covered || !cell.text || cell.colSpan > 1 || merged.has(`${row.number}:${cell.column}`)) continue
      if (cell.style.textAlign !== 'left' || cell.style.whiteSpace === 'pre-wrap') continue

      let width = columns[cell.column - 1].width
      const needed = textPixels(cell.text, cell.style) + 6
      let span = 1

      while (width < needed && cell.column + span <= columnCount) {
        const next = row.cells[cell.column + span - 1]
        if (next.text || next.covered || merged.has(`${row.number}:${next.column}`)) break
        if (next.style.backgroundColor !== cell.style.backgroundColor) break
        width += columns[next.column - 1].width
        next.covered = true
        span++
      }

      cell.colSpan = span
    }
  }

  return {
    name: sheet.name,
    columns,
    rows,
    totalRows,
    frozenRows: frozen ? Math.min(view?.ySplit ?? 0, rowCount) : 0,
    frozenColumns: frozen ? Math.min(view?.xSplit ?? 0, columnCount) : 0,
    gridLines: view?.showGridLines !== false,
  }
}

type ExcelModule = typeof ExcelJs

/**
 * ExcelJS is large, so it is loaded on first use rather than bundled into every
 * page. Its browser build is UMD, so the namespace may arrive under `default`.
 */
export async function loadExcelJs(): Promise<ExcelModule> {
  const module = await import('exceljs')
  return (module as ExcelModule & { default?: ExcelModule }).default ?? module
}

export async function readWorkbook(blob: Blob): Promise<GridSheet[]> {
  const ExcelJS = await loadExcelJs()
  const workbook: Workbook = new ExcelJS.Workbook()

  await workbook.xlsx.load(await blob.arrayBuffer())

  return workbook.worksheets.filter((sheet) => sheet.state !== 'veryHidden' && sheet.state !== 'hidden').map(readSheet)
}
